import React, { useMemo } from "react";
import { ImageBackground, Platform, StyleSheet } from "react-native";
import { Box } from "components/ui";
import { Element } from "@craftjs/core";
import { LinearGradient } from "expo-linear-gradient";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { PageText } from "./PageText";
import { PageButton } from "./PageButton";
import defaultImage from "../../../assets/slider-panel-default-image.webp";
import { Helmet } from "react-helmet";

// ---- Craft-compatible components ----

export const PanelTitle = ({ children, ...props }: any) => {
  return <Box {...props}>{children}</Box>;
};

export const PanelButtonContainer = ({ children, ...props }: any) => {
  return (
    <Box {...props} style={{ padding: "10px 0" }}>
      {children}
    </Box>
  );
};

// ---- Helper to rewrite API URL to CDN ----

const rewriteImageUrlToCDN = (url: string): string => {
  const apiUrl = process.env.EXPO_PUBLIC_ABCO_API_URL;
  const cdnUrl = process.env.EXPO_PUBLIC_ABCO_API_CDNURL;
  if (url?.startsWith(apiUrl)) {
    return url.replace(apiUrl, cdnUrl);
  }
  return url;
};

// ---- Main Craft-rendered component ----

export const PageImageSliderPanel = React.memo(({ imageUrl }: any) => {
  const isMobile = useIsMobile();

  const effectiveImageUrl = imageUrl
    ? `${rewriteImageUrlToCDN(imageUrl)}?width=800&quality=85&format=webp`
    : defaultImage;

  const textContent = useMemo(() => (
    <Box alignItems="center" justifyContent="center" flex={1}>
      <Box paddingHorizontal={isMobile ? 24 : 48} pt={64} pb={120} justifyContent="center" flex={1}>
        <Element canvas id="panel-title" is={PanelTitle} data-cy="slider-title-container">
          <PageText text="Headline" fontSize={20} data-cy="slider-title" />
          <PageText text="Subtext" fontSize={15} data-cy="slider-subtext" />
        </Element>
        <Box width="$full">
          <Element canvas id="buttons-slider-container" is={PanelButtonContainer} data-cy="slider-button-container">
            <PageButton variant="primary" size="small" text="Button" data-cy="button-slider" />
          </Element>
        </Box>
      </Box>
    </Box>
  ), [isMobile]);

  const imageContent = useMemo(() => {
    if (Platform.OS === "web") {
		return (
			<>
			<Box
				flex={1}
				position="relative"
				overflow="hidden"
				style={{
				minHeight: 578,
				width: "100%",
				backgroundImage: `url(${effectiveImageUrl})`,
				backgroundSize: "cover",
				backgroundPosition: "center",
				backgroundRepeat: "no-repeat",
				}}
			>
				{!isMobile && (
				<Box
					style={{
					position: "absolute",
					left: 0,
					top: 0,
					bottom: 0,
					width: "20%",
					background: "linear-gradient(to right, #E3F1FA, transparent)",
					}}
				/>
				)}
			</Box>
			</>
		);
		}

    return (
      <Box justifyContent="center" alignItems="center" flex={1}>
        <ImageBackground
          source={imageUrl ? { uri: effectiveImageUrl } : defaultImage}
          resizeMode="cover"
          style={{ flex: 1, width: "100%", minHeight: 578 }}
        >
          <LinearGradient
            start={isMobile ? { x: 0, y: 1 } : { x: 0, y: 1 }}
            end={isMobile ? { x: 0, y: 0.8 } : { x: 1, y: 1 }}
            colors={["#E3F1FA", "transparent"]}
            style={
              isMobile
                ? StyleSheet.absoluteFillObject
                : {
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "20%",
                  }
            }
          />
        </ImageBackground>
      </Box>
    );
  }, [isMobile, imageUrl]);

  return (
    <>
      <Box
        flexDirection={isMobile ? "column" : "row"}
        borderRadius={12}
        minHeight={578}
        overflow="hidden"
        justifyContent="center"
        flex={1}
        bg="#E3F1FA"
      >
        {isMobile ? (
          <>
            {imageContent}
            {textContent}
          </>
        ) : (
          <>
            {textContent}
            {imageContent}
          </>
        )}
      </Box>
    </>
  );
});

// Optional default props for Craft
export const PageImageSliderPanelDefaultProps = {
  background: "https://via.assets.so/img.jpg?w=500&h=250&tc=gray&bg=white&t=Upload Image",
};

// You must register this in your main Editor resolver:
export const PageImageSliderPanelResolver = {
  PageImageSliderPanel,
  PanelTitle,
  PanelButtonContainer,
};
